import type { Params } from 'react-router';
import { matchPath } from 'react-router';

import { PARAMS, RIGHTS_ACCESS } from 'constants/RightsConstants';
import type { RightsMatrix, RightsRequirement } from 'constants/RightsConstants';
import { PATH } from 'constants/RoutesConstants';
import { ADMIN, GROUP_MANAGER, MANUFACTURER } from 'constants/UserConstants';
import type { MayBe } from 'modules/global/typing/common/common';
import { getCurrentRoute, getRoutePath } from 'services/RouterUtils';

import { hasAccess } from './utils/hasAccess';

export const isManufacturer = (userRoles: MayBe<string[]>): boolean => hasAccess(userRoles, [MANUFACTURER]);

export const isAdmin = (userRoles: MayBe<string[]>): boolean => hasAccess(userRoles, [ADMIN]);

export const isGroupManager = (userRoles: MayBe<string[]>): boolean => hasAccess(userRoles, [GROUP_MANAGER]);

/** Whether the matrix holds, for every right the requirement lists, one of the values it allows. */
export const checkPermissions = (requirement: RightsRequirement, matrix: RightsMatrix): boolean => {
  const heldByRight: Partial<Record<string, RightsMatrix[keyof RightsMatrix]>> = matrix;
  return Object.entries(requirement).every(([right, values]) => !values?.length || values.some(value => heldByRight[right]?.includes(value)));
};

/**
 * Whether the matrix grants access to an entity of `RIGHTS_ACCESS`. An entity without a rule is
 * granted: an unmapped route is public, not denied.
 */
export const checkRights = (entity: MayBe<string> | null, matrix: RightsMatrix, routeParameters: Params = {}): boolean => {
  const { extends: inheritance, excludes, [PARAMS]: params, ...requirement } = (entity ? RIGHTS_ACCESS[entity] : undefined) ?? {};
  const hasRights = checkPermissions(requirement, matrix);

  const haveExcludes = typeof excludes === 'object' && Object.keys(excludes).length > 0;

  const hasValidParams = Object.entries(routeParameters).every(([key, value]) => {
    const paramsRights = value ? params?.[key]?.[value] : undefined;
    return paramsRights ? checkPermissions(paramsRights, matrix) : true;
  });

  return (
    hasRights &&
    hasValidParams &&
    (!haveExcludes || !checkPermissions(excludes, matrix)) &&
    (!inheritance || checkRights(inheritance, matrix, routeParameters))
  );
};

/** Whether the matrix grants access to the route a pathname resolves to, its parameters included. */
export const haveRouteAccess = (pathname: string, matrix: RightsMatrix): boolean => {
  const entity = getCurrentRoute(pathname);
  const pathKey = getRoutePath(pathname);
  const match = pathKey ? matchPath(PATH[pathKey].path, pathname) : null;

  return checkRights(entity, matrix, match?.params);
};
